import { Box, Typography, Paper, Switch } from '@mui/material';
import eventBus, { eventBusValues } from '../../eventBus';
import { useAccount } from '../../contexts/Account';
import dateFormat from '../../utils/dateFormat';
import { styled } from '@mui/material/styles';
import Logo from '../../icons/logo.svg';

const OnOffSwitch = styled(Switch)(({ theme }) => ({
  width: 68,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translateX(4px)',
    '&.Mui-checked': {
      transform: 'translateX(38px)',
      '& + .MuiSwitch-track': {
        backgroundColor: '#DBF7EB',
        opacity: 1,
        '&:before': {
          content: '"On"',
          left: '8px',
          right: 'auto',
        },
      },
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: '#fff',
    width: 22,
    height: 22,
    boxShadow: 'none',
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9E9',
    opacity: 1,
    position: 'relative',
    '&:before': {
      content: '"Off"',
      position: 'absolute',
      right: '8px',
      top: '50%',
      transform: 'translateY(-50%)',
      fontSize: 14,
      color: '#232B35',
      fontWeight: '600',
    },
  },
}));

export function MyOnOffSwitch({ id, checked, handleChange, disabled = false }) {
  return (
    <OnOffSwitch
      id={id}
      checked={checked}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

const styles = {
  switchLabel: {
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: '100%',
    color: '#232B35',
  },
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
};

const MembershipTile = () => {
  const { accountDetails, currentPlan, isFreeTrial, isFreemium } = useAccount();
  const tier = currentPlan?.salesforceValues?.tier;
  const dateObj = accountDetails.createdAt
    ? new Date(accountDetails.createdAt)
    : new Date();
  const year = dateFormat(dateObj, 'yyyy');
  const month = dateFormat(dateObj, 'mmmm');
  const day = dateFormat(dateObj, 'dd');
  const memberSince = `Advisor since ${month} ${day}, ${year}.`;

  function getBillingCycleValue() {
    if (tier) {
      return tier + ' Membership';
    }
    return 'Membership';
  }

  function goToUpgradePage(origin = '') {
    if (isFreeTrial) {
      eventBus.dispatch(eventBusValues.triggerAdvisorUpgradeAction, { origin });
    }
    // else {
    //   window.open(
    //     `${accountDetails.upgradeUrl}?iwm_advisor_id=${accountDetails.iwmId}&sosfcontactid=${accountDetails.salesforceContactId}&email=${accountDetails.email}&plancode=${accountDetails.recurlyPlanCode}`
    //   );
    // }
  }

  return (
    <Paper
      id="membership-tile"
      elevation={3}
      sx={{
        padding: '15px',
        borderRadius: '8px',
        backgroundColor: '#e5f3ff',
        boxShadow: 'none',
        display: 'flex',
        justifyContent: 'space-around',
        flexDirection: {
          xxs: 'column',
          xs: 'row',
          md: 'column',
        },
      }}
    >
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '10px',
            marginBottom: '5px',
          }}
        >
          <img
            src={Logo}
            alt="right"
            style={{ width: '40px', height: '40px', borderRadius: '50%' }}
          />
          <Typography
            style={{
              fontSize: '16px',
              lineHeight: '100%',
              fontWeight: 500,
              color: '#232B35',
              fontFamily: 'Poppins',
              marginTop: '5px',
            }}
          >
            AdvisoryCloud Account
          </Typography>
        </Box>
        <Typography
          style={{
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: '24px',
            color: '#232B35',
            fontFamily: 'Poppins',
            letterSpacing: '0px',
          }}
        >
          {isFreemium ? 'Starter' : getBillingCycleValue()}
        </Typography>
        <Typography
          style={{
            fontWeight: 400,
            fontSize: '12px',
            color: '#232B35',
            fontFamily: 'Poppins',
            letterSpacing: '0.5px',
            marginTop: '5px',
          }}
        >
          {memberSince}
        </Typography>
        <Typography
          onClick={() => goToUpgradePage('see-all-upgrade-options')}
          id="accountVitalsOptionsText"
          style={{
            fontSize: '12px',
            marginTop: '5px',
            cursor: 'pointer',
            color: '#3171F6',
            fontWeight: 700,
            letterSpacing: '0.5px',
          }}
        >
          See all upgrade options
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '10px',
          // mt: '10px',
          minWidth: '250px',
        }}
      >
        <Box sx={styles.switchContainer}>
          <Typography style={styles.switchLabel} id="accountVitalsProfileText">
            Advisor Profile Active
          </Typography>
          <MyOnOffSwitch
            id="accountVitalsProfileToggle"
            checked={!isFreemium}
            disabled={!isFreemium}
            handleChange={() => {
              if (!isFreemium) {
                return;
              } else {
                goToUpgradePage('activate-profile');
              }
            }}
          />
        </Box>
        <Box sx={styles.switchContainer}>
          <Typography
            style={styles.switchLabel}
            id="accountVitalsUnlimitedText"
          >
            Join Unlimited Boards
          </Typography>
          <MyOnOffSwitch
            id="accountVitalsUnlimitedToggle"
            checked={currentPlan?.boardsCanJoinCount == -1}
            disabled={currentPlan?.boardsCanJoinCount == -1}
            handleChange={() => {
              if (currentPlan?.boardsCanJoinCount == -1) {
                return;
              } else {
                goToUpgradePage('join-unlimited-boards');
              }
            }}
          />
        </Box>
        <Box sx={styles.switchContainer}>
          <Typography style={styles.switchLabel} id="accountVitalsVIPText">
            Get VIP Access & Service
          </Typography>
          <MyOnOffSwitch
            id="accountVitalsVIPToggle"
            checked={tier === 'VIP'}
            disabled={tier === 'VIP'}
            handleChange={() => {
              if (tier === 'VIP') {
                return;
              } else {
                goToUpgradePage('get-vip-access');
              }
            }}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default MembershipTile;
